import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui";
import {
  useTenantActivation,
  useTenantAddMembers,
  useTenantChangeOwner,
  useTenantDetail,
  useTenantInstallProduct,
  useTenantRemoveMembers,
  useTenantUpdateMemberRole,
  useTenantUpdateProductVisibility,
} from "~/features/admin/tenants/hooks";
import type {
  FilterUserOption,
  TenantMember,
  TenantProductConnection,
  TenantProductVisibility,
} from "~/features/admin/tenants/types";
import { toTenantDetailUI } from "~/features/admin/tenants/utils/transformers";
import { buildTenantRootUrl } from "~/features/tenant/utils";
import { AddMembersDialog } from "./AddMembersDialog";
import { ChangeOwnerDialog } from "./ChangeOwnerDialog";
import type { InstallProductOption } from "./InstallProductDialog";
import { InstallProductDialog } from "./InstallProductDialog";
import { TenantActivationDialog } from "./TenantActivationDialog";
import { TenantDetailPresenter } from "./TenantDetailPresenter";
import { TenantFormDialog } from "./TenantFormDialog";

interface TenantDetailProps {
  id: string;
  /** Override for the back button (close-the-sheet vs. navigate to list). */
  onBack: () => void;
}

/** Reusable from both the full-page route and the Peek side panel. Owns data + flows. */
export function TenantDetail({ id, onBack }: TenantDetailProps) {
  const { t } = useTranslation();
  const { data } = useTenantDetail(id);
  const { onActivate, onDeactivate, processing: activationProcessing } = useTenantActivation();
  const { submit: submitAddMembers, processing: addProcessing } = useTenantAddMembers({
    onSuccess: () => setAddOpen(false),
  });
  const { submit: submitRemoveMembers } = useTenantRemoveMembers();
  const { submit: submitUpdateMemberRole, processing: memberRoleProcessing } = useTenantUpdateMemberRole();
  const { submit: submitChangeOwner, processing: changeOwnerProcessing } = useTenantChangeOwner();
  const { submit: submitInstallProduct, processing: installProcessing } = useTenantInstallProduct();
  const { submit: submitUpdateVisibility, processing: visibilityProcessing } = useTenantUpdateProductVisibility();

  const [pendingAction, setPendingAction] = useState<"activate" | "deactivate" | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<TenantMember | null>(null);
  const [changeOwnerOpen, setChangeOwnerOpen] = useState(false);
  const [pendingOwner, setPendingOwner] = useState<FilterUserOption | null>(null);
  const [installOpen, setInstallOpen] = useState(false);
  const [pendingInstall, setPendingInstall] = useState<InstallProductOption | null>(null);
  const [pendingVisibility, setPendingVisibility] = useState<{
    connection: TenantProductConnection;
    visibility: TenantProductVisibility;
  } | null>(null);

  const raw = data.tenantsByPk;
  const tenant = useMemo(() => (raw ? toTenantDetailUI(raw) : null), [raw]);

  if (!tenant) {
    return (
      <div className="rounded-md border border-border-default bg-white px-5 py-12 text-center text-14-regular text-fg-2">
        —
      </div>
    );
  }

  const tenantUrl =
    buildTenantRootUrl({
      tenantShortCode: tenant.shortCode,
      href: window.location.href,
    })?.toString() ?? null;

  const handleConfirmActivation = async () => {
    if (pendingAction === "activate") {
      await onActivate({ id: tenant.id });
    } else if (pendingAction === "deactivate") {
      await onDeactivate({ id: tenant.id });
    }
    setPendingAction(null);
  };

  const handleConfirmRemove = async () => {
    if (pendingRemove) {
      await submitRemoveMembers(tenant.id, [pendingRemove.id]);
    }
    setPendingRemove(null);
  };

  const handleConfirmChangeOwner = async () => {
    if (pendingOwner) {
      await submitChangeOwner(tenant.id, pendingOwner.id);
    }
    setPendingOwner(null);
  };

  const ownerLabel = (user: { displayName: string | null; email: string }) => user.displayName ?? user.email;

  const handleConfirmInstall = async () => {
    if (pendingInstall) {
      await submitInstallProduct(tenant.id, pendingInstall.id);
    }
    setPendingInstall(null);
  };

  const handleConfirmVisibility = async () => {
    if (pendingVisibility) {
      await submitUpdateVisibility(tenant.id, pendingVisibility.connection.productId, pendingVisibility.visibility);
    }
    setPendingVisibility(null);
  };

  return (
    <>
      <TenantDetailPresenter
        tenant={tenant}
        tenantUrl={tenantUrl}
        onBack={onBack}
        onEdit={() => setEditOpen(true)}
        onActivate={() => setPendingAction("activate")}
        onDeactivate={() => setPendingAction("deactivate")}
        onAddMember={() => setAddOpen(true)}
        onRemoveMember={setPendingRemove}
        onChangeMemberRole={(member, role) => submitUpdateMemberRole(tenant.id, member.id, role)}
        onChangeOwner={() => setChangeOwnerOpen(true)}
        onInstallProduct={() => setInstallOpen(true)}
        onChangeVisibility={(connection, visibility) => setPendingVisibility({ connection, visibility })}
        processing={activationProcessing || installProcessing || visibilityProcessing || memberRoleProcessing}
      />

      <TenantActivationDialog
        open={pendingAction !== null}
        action={pendingAction ?? "deactivate"}
        name={tenant.name}
        processing={activationProcessing}
        onConfirm={handleConfirmActivation}
        onCancel={() => setPendingAction(null)}
      />

      <TenantFormDialog open={editOpen} onOpenChange={setEditOpen} tenantId={tenant.id} />

      <AddMembersDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        organizationId={tenant.organizationId}
        currentMembers={tenant.members}
        processing={addProcessing}
        onSubmit={(userIds) => submitAddMembers(tenant.id, userIds)}
      />

      <ChangeOwnerDialog
        open={changeOwnerOpen}
        onClose={() => setChangeOwnerOpen(false)}
        organizationId={tenant.organizationId}
        currentOwner={tenant.owner}
        onSelect={(user) => {
          setChangeOwnerOpen(false);
          setPendingOwner(user);
        }}
      />

      <AlertDialog open={pendingOwner !== null} onOpenChange={(next) => !next && setPendingOwner(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="capitalize">{t("feature.tenants.changeOwner")}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingOwner &&
                (tenant.owner
                  ? t("feature.tenants.confirmChangeOwner", {
                      from: ownerLabel(tenant.owner),
                      to: ownerLabel(pendingOwner),
                    })
                  : t("feature.tenants.confirmSetOwner", { to: ownerLabel(pendingOwner) }))}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={changeOwnerProcessing}>{t("ui.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmChangeOwner} disabled={changeOwnerProcessing}>
              {t("feature.tenants.changeOwner")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <InstallProductDialog
        open={installOpen}
        onClose={() => setInstallOpen(false)}
        connections={tenant.productConnections}
        onSelect={(product) => {
          setInstallOpen(false);
          setPendingInstall(product);
        }}
      />

      <AlertDialog open={pendingInstall !== null} onOpenChange={(next) => !next && setPendingInstall(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="capitalize">{t("feature.tenants.installProduct")}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingInstall && t("feature.tenants.confirmInstallProduct", { product: pendingInstall.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={installProcessing}>{t("ui.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmInstall} disabled={installProcessing}>
              {t("feature.tenants.installProduct")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingVisibility !== null} onOpenChange={(next) => !next && setPendingVisibility(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="capitalize">{t("feature.tenants.changeVisibility")}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingVisibility &&
                t("feature.tenants.confirmChangeVisibility", {
                  product: pendingVisibility.connection.productName,
                  from: pendingVisibility.connection.visibility,
                  to: pendingVisibility.visibility,
                })}
              {pendingVisibility?.visibility === "authenticated" && (
                <>
                  <br />
                  {t("feature.tenants.visibilityAuthenticatedWarning")}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={visibilityProcessing}>{t("ui.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmVisibility} disabled={visibilityProcessing}>
              {t("feature.tenants.changeVisibility")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingRemove !== null} onOpenChange={(next) => !next && setPendingRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("ui.deleteX", {
                x: pendingRemove?.displayName || pendingRemove?.email || "",
              })}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("ui.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRemove}>{t("ui.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
